import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useCms } from '../contexts/CmsContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e6aed3a5-a232-432c-8b8d-e52fe226bdbc/artifacts/y54ymzlq_The%20SSMCH_Logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Academics', path: '/academics' },
  { name: 'Hospital', path: '/hospital' },
  { name: 'Admissions', path: '/admissions', highlight: true },
  { name: 'Campus Life', path: '/campus-life' },
  { name: 'Faculty', path: '/faculty' },
  { name: 'News', path: '/news' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'NMC Info', path: '/nmc-disclosures' },
  { name: 'Careers', path: '/careers', highlight: true, variant: 'teal' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useCms();

  const phone = settings?.contact?.phone || '+91-9303841084';
  const email = settings?.contact?.emails?.admissions || 'admissions@ssmch.in';
  const socialLinks = settings?.social_links || {
    facebook: 'https://www.facebook.com/share/17Gp1FVd9i/',
    instagram: 'https://www.instagram.com/ssmch_jabalpur',
    youtube: 'https://youtube.com/@sukhsagarmedicalcollegeand6013',
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="hidden xl:block bg-[#1A202C] text-white text-sm py-2">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="flex items-center gap-2 hover:text-[#4ECDC4] transition-colors" data-testid="header-phone">
              <Phone size={14} />
              <span>{phone}</span>
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-[#4ECDC4] transition-colors" data-testid="header-email">
              <Mail size={14} />
              <span>{email}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-400">Follow us:</span>
            <TooltipProvider delayDuration={200}>
              <div className="social-icons-container">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={socialLinks.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon social-icon-header" 
                      aria-label="Facebook"
                      data-testid="header-facebook"
                    >
                      <Facebook size={14} />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Follow us on Facebook</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={socialLinks.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon social-icon-header" 
                      aria-label="Instagram"
                      data-testid="header-instagram"
                    >
                      <Instagram size={14} />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Follow us on Instagram</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={socialLinks.youtube} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon social-icon-header" 
                      aria-label="YouTube"
                      data-testid="header-youtube"
                    >
                      <Youtube size={14} />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Subscribe on YouTube</p></TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-md' 
            : 'bg-white'
        }`}
        data-testid="main-header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="SSMCH Logo" 
                className="h-14 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    link.highlight && link.variant === 'teal'
                      ? 'text-[#4ECDC4] hover:text-[#3DBDB4]'
                      : link.highlight
                      ? 'text-[#D4A84B] hover:text-[#4ECDC4]'
                      : isActive(link.path)
                      ? 'text-[#4ECDC4]'
                      : 'text-gray-700 hover:text-[#4ECDC4]'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="xl:hidden p-2 text-gray-700 hover:text-[#4ECDC4] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
            <div className="container-custom py-4">
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      link.highlight && link.variant === 'teal'
                        ? 'text-[#4ECDC4] bg-[#4ECDC4]/5'
                        : link.highlight
                        ? 'text-[#D4A84B] bg-[#D4A84B]/5'
                        : isActive(link.path)
                        ? 'text-[#4ECDC4] bg-[#4ECDC4]/5'
                        : 'text-gray-700 hover:text-[#4ECDC4] hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex flex-col gap-3 text-sm text-gray-600">
                  <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="flex items-center gap-2">
                    <Phone size={14} className="text-[#4ECDC4]" />
                    <span>{phone}</span>
                  </a>
                  <a href={`mailto:${email}`} className="flex items-center gap-2">
                    <Mail size={14} className="text-[#4ECDC4]" />
                    <span>{email}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useCms();

  const phone = settings?.contact?.phone || '+91-9303841084';
  const email = settings?.contact?.emails?.admissions || 'admissions@ssmch.in';
  const address = settings?.address || {};
  const socialLinks = settings?.social_links || {};

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'MBBS Program', path: '/academics' },
    { name: 'Admissions', path: '/admissions' },
    { name: 'Hospital', path: '/hospital' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];

  const resourceLinks = [
    { name: 'News & Updates', path: '/news' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'NMC Disclosures', path: '/nmc-disclosures' },
    { name: 'Faculty', path: '/faculty' },
    { name: 'Campus Life', path: '/campus-life' },
  ];

  return (
    <footer className="bg-[#1A202C] text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="inline-flex items-center bg-white rounded-xl px-4 py-3 mb-6 shadow-sm">
              <img
                src={LOGO_URL}
                alt="SSMCH Logo"
                className="h-14 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Sukh Sagar Medical College & Hospital is committed to excellence in medical education, research, and patient care.
            </p>
            <p className="text-gray-500 text-xs mb-3">Connect with us</p>
            <TooltipProvider delayDuration={200}>
              <div className="social-icons-container">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={socialLinks.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon social-icon-footer" 
                      aria-label="Facebook"
                      data-testid="footer-facebook"
                    >
                      <Facebook size={18} />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Follow us on Facebook</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={socialLinks.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon social-icon-footer" 
                      aria-label="Instagram"
                      data-testid="footer-instagram"
                    >
                      <Instagram size={18} />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Follow us on Instagram</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={socialLinks.youtube} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-icon social-icon-footer" 
                      aria-label="YouTube"
                      data-testid="footer-youtube"
                    >
                      <Youtube size={18} />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Subscribe on YouTube</p></TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-[#4ECDC4] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-white">Resources</h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-[#4ECDC4] transition-colors text-sm"
                    data-testid={`footer-resource-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#4ECDC4] flex-shrink-0 mt-1" />
                <span className="text-gray-400 text-sm">
                  {address.line1 || 'Sukh Sagar Medical College & Hospital'},{' '}<br />
                  {address.line2 || 'Mukanwara, Mouza, Chargawan'},{' '}<br />
                  {address.city || 'Jabalpur'}, {address.state || 'Madhya Pradesh'} {address.pincode || '482003'}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#4ECDC4] flex-shrink-0" />
                <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="text-gray-400 hover:text-[#4ECDC4] transition-colors text-sm">
                  {phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#4ECDC4] flex-shrink-0" />
                <a href={`mailto:${email}`} className="text-gray-400 hover:text-[#4ECDC4] transition-colors text-sm">
                  {email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {currentYear} {settings?.college_name || 'Sukh Sagar Medical College & Hospital'}. All rights reserved.
            </p>

            {/* Credit — Dept of Data & Knowledge Management, SSMCH */}
            <div
              className="flex items-center gap-3 text-gray-400 text-xs"
              data-testid="ddkm-credit"
              title="This website made & maintained by Dept of Data & Knowledge Management"
            >
              {/* Logo block: 3-circle mark + 'data = TRUST' punchline */}
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 40 40"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle cx="20" cy="12" r="9" fill="#1BA88A" opacity="0.95" />
                  <circle cx="13" cy="24" r="9" fill="#A6D64C" opacity="0.9" />
                  <circle cx="27" cy="24" r="9" fill="#8BC63F" opacity="0.9" />
                  <circle cx="20" cy="20" r="6.5" fill="#E9A93D" />
                </svg>
                <span
                  className="bg-[#F6E7B4]/90 text-[#1A202C] px-1.5 py-0.5 rounded-sm text-[9px] leading-none tracking-wide font-medium"
                  data-testid="ddkm-punchline"
                >
                  data = <span className="font-bold text-[#8a6d1a]">TRUST</span>
                </span>
              </div>

              <span className="leading-snug text-sm">
                This website made &amp; maintained by
                <br className="hidden sm:block" />{' '}
                <span className="font-semibold text-gray-200">
                  Dept of Data &amp; Knowledge Management
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
